import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Undo2, X } from 'lucide-react';
import { api, type Column, type Rec } from '../lib/api';
import { keys } from '../lib/queries';
import { R, chip, colLabel, ft, label, mono, plural } from '../lib/ui';
import { useDialog } from './DialogHost';

/** Значение в строку: пустое и null показываем одинаково. */
const text = (value: unknown) =>
  value === null || value === undefined ? '' : String(value);

interface Entry {
  key: string;
  recordId: string | null;
  colId: string;
  colLabel: string;
  address: string;
  from: string;
  to: string;
  fromRaw: unknown;
  who: string;
  at: Date;
  op: string;
}

export function HistoryPanel({
  docId, tableId, columns, records, onClose, onGoToCell,
}: {
  docId: string;
  tableId: string;
  columns: Column[];
  records: Rec[];
  onClose: () => void;
  onGoToCell?: (recordId: string, colId: string) => void;
}) {
  const qc = useQueryClient();
  const dialog = useDialog();

  const [onlyMine, setOnlyMine] = useState(false);
  const [limit, setLimit] = useState(50);

  const me = useQuery({ queryKey: ['me'], queryFn: api.me }).data;

  const history = useQuery({
    queryKey: ['table-history', docId, tableId, limit],
    queryFn: () => api.tableHistory(docId, tableId, limit),
    // Правки соседей подтягиваются сами, раз в полминуты.
    refetchInterval: 30_000,
    placeholderData: (prev) => prev,
  });

  const revert = useMutation({
    mutationFn: (entry: Entry) =>
      api.patchRecords(docId, tableId, [
        { id: entry.recordId!, fields: { [entry.colId]: entry.fromRaw ?? null } },
      ]),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: keys.records(docId, tableId) });
      qc.invalidateQueries({ queryKey: ['table-history', docId, tableId] });
    },
    onError: (e) =>
      void dialog.alert({ title: 'Не удалось откатить', body: (e as Error).message }),
  });

  const rowIndex = useMemo(() => {
    const map = new Map<string, number>();
    records.forEach((rec, i) => map.set(rec.id, i));
    return map;
  }, [records]);

  const colIndex = useMemo(() => {
    const map = new Map<string, number>();
    columns.forEach((col, i) => map.set(col.fields.colId, i));
    return map;
  }, [columns]);

  /** Одна ревизия разворачивается в отдельную запись на каждое изменённое поле. */
  const entries = useMemo<Entry[]>(() => {
    const out: Entry[] = [];

    const labelOf = (colId: string) =>
      history.data?.columns.find((c) => c.id === colId)?.label ??
      columns.find((c) => c.fields.colId === colId)?.fields.label ??
      colId;

    for (const rev of history.data?.revisions ?? []) {
      const who = rev.actor?.name ?? 'Система';
      const at = new Date(rev.at);
      const row = rev.recordId ? rowIndex.get(rev.recordId) : undefined;

      const addressOf = (colId: string) => {
        const col = colIndex.get(colId);
        return row !== undefined && col !== undefined ? `${colLabel(col)}${row + 1}` : '';
      };

      if (rev.op === 'update' && rev.before && rev.after) {
        for (const [colId, after] of Object.entries(rev.after)) {
          const before = rev.before[colId];
          if (text(before) === text(after)) continue;

          out.push({
            key: `${rev.id}:${colId}`,
            recordId: rev.recordId,
            colId,
            colLabel: labelOf(colId),
            address: addressOf(colId),
            from: text(before),
            to: text(after),
            fromRaw: before,
            who,
            at,
            op: rev.op,
          });
        }
      } else if (rev.op === 'insert') {
        out.push({
          key: `${rev.id}:+`,
          recordId: rev.recordId,
          colId: '',
          colLabel: '',
          address: row !== undefined ? `строка ${row + 1}` : 'строка',
          from: '',
          to: 'добавлена',
          fromRaw: null,
          who,
          at,
          op: rev.op,
        });
      } else if (rev.op === 'delete') {
        out.push({
          key: `${rev.id}:-`,
          recordId: rev.recordId,
          colId: '',
          colLabel: '',
          address: 'строка',
          from: 'была',
          to: 'удалена',
          fromRaw: null,
          who,
          at,
          op: rev.op,
        });
      }
    }

    return onlyMine && me ? out.filter((e) => e.who === me.name) : out;
  }, [history.data, columns, rowIndex, colIndex, onlyMine, me]);

  // Счётчик внизу считает ревизии, а не развёрнутые записи.
  const loaded = history.data?.revisions.length ?? 0;

  const edits = entries.filter((e) => e.op === 'update').length;
  const rows = entries.length - edits;

  return (
    <aside
      style={{
        width: 360,
        flex: '0 0 360px',
        display: 'flex',
        flexDirection: 'column',
        background: R.bgSubtle,
        borderLeft: ft.strong,
        minHeight: 0,
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 8,
          padding: '16px 18px 12px',
          borderBottom: ft.strong,
        }}
      >
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={label}>История</div>
          <div style={{ fontWeight: 800, fontSize: 17, marginTop: 2, color: R.text }}>
            Версии и правки
          </div>
        </div>
        <button
          onClick={onClose}
          title="Закрыть"
          style={{
            width: 26,
            height: 26,
            display: 'grid',
            placeItems: 'center',
            color: R.textMuted,
            background: 'transparent',
          }}
          onMouseEnter={(e) => (e.currentTarget.style.background = R.bgElevated)}
          onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
        >
          <X size={15} />
        </button>
      </div>

      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          minHeight: 40,
          padding: '6px 18px',
          borderBottom: ft.hair,
          flexWrap: 'wrap',
        }}
      >
        <span style={{ ...chip(true), cursor: 'default' }}>
          {edits} {plural(edits, 'правка', 'правки', 'правок')}
        </span>
        {rows > 0 && (
          <span style={{ ...chip(false), cursor: 'default' }}>
            {rows} {plural(rows, 'строка', 'строки', 'строк')}
          </span>
        )}
        <button style={chip(onlyMine)} onClick={() => setOnlyMine((v) => !v)}>
          только мои
        </button>
      </div>

      <div className="rt-scroll" style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
        {history.isLoading && (
          <div style={{ padding: 18, fontSize: 12.5, color: R.textMuted }}>Загрузка…</div>
        )}
        {history.error && (
          <div style={{ padding: 18, fontSize: 12.5, color: R.danger }}>
            {(history.error as Error).message}
          </div>
        )}
        {!history.isLoading && entries.length === 0 && (
          <div style={{ padding: 18, fontSize: 12.5, color: R.textMuted }}>Правок пока нет</div>
        )}

        {entries.map((entry) => (
          <div
            key={entry.key}
            onClick={() => entry.colId && entry.recordId && onGoToCell?.(entry.recordId, entry.colId)}
            style={{
              padding: '12px 18px',
              borderBottom: ft.hair,
              borderLeft: '3px solid transparent',
              display: 'flex',
              flexDirection: 'column',
              gap: 4,
              cursor: entry.colId ? 'pointer' : 'default',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = R.selectionSubtle;
              e.currentTarget.style.borderLeftColor = R.accent;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'transparent';
              e.currentTarget.style.borderLeftColor = 'transparent';
            }}
          >
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
              <span style={{ fontSize: 11, fontFamily: mono, color: R.textMuted }}>
                {entry.at.toLocaleTimeString('ru', { hour: '2-digit', minute: '2-digit' })}
              </span>
              <span
                style={{
                  fontSize: 12.5,
                  fontWeight: 600,
                  color: R.text,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {entry.who}
              </span>
              <span
                style={{
                  marginLeft: 'auto',
                  fontSize: 11,
                  color: R.textMuted,
                  whiteSpace: 'nowrap',
                }}
              >
                {entry.colLabel
                  ? `${entry.colLabel}${entry.address ? ` · ${entry.address}` : ''}`
                  : entry.address}
              </span>
            </div>

            <div style={{ fontSize: 12.5, color: R.textMuted, wordBreak: 'break-word' }}>
              {entry.from && <span>{entry.from}</span>}
              {entry.from && ' → '}
              <span style={{ color: R.text }}>{entry.to || 'пусто'}</span>
            </div>

            {entry.op === 'update' && (
              <button
                onClick={async (e) => {
                  e.stopPropagation();
                  const ok = await dialog.confirm({
                    title: 'Вернуть прежнее значение?',
                    body:
                      `В ячейку ${entry.colLabel}` +
                      `${entry.address ? ` (${entry.address})` : ''} ` +
                      `вернётся «${entry.from || 'пусто'}».`,
                    confirmLabel: 'Вернуть',
                  });
                  if (ok) revert.mutate(entry);
                }}
                style={{
                  alignSelf: 'flex-start',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 5,
                  fontSize: 11.5,
                  color: R.accentText,
                  padding: 0,
                  marginTop: 2,
                  background: 'transparent',
                }}
              >
                <Undo2 size={11} /> вернуть
              </button>
            )}
          </div>
        ))}
      </div>

      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          padding: '12px 18px',
          borderTop: ft.strong,
          fontSize: 12,
          color: R.textMuted,
        }}
      >
        <span>
          Показано {Math.min(limit, loaded)}{' '}
          {plural(Math.min(limit, loaded), 'правка', 'правки', 'правок')}
        </span>

        {loaded >= limit && limit < 500 && (
          <button
            onClick={() => setLimit((v) => v + 100)}
            disabled={history.isFetching}
            style={{
              marginLeft: 'auto',
              fontSize: 11.5,
              padding: 0,
              color: R.accentText,
              background: 'transparent',
              opacity: history.isFetching ? 0.5 : 1,
            }}
          >
            {history.isFetching ? 'загружаем…' : 'показать ещё'}
          </button>
        )}
      </div>
    </aside>
  );
}
